import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Tooltip, Legend } from 'recharts';
import { Settings, BarChart3, Menu, Plus, MapPin, FileText, Users, Trash2, AlertTriangle, CheckCircle, XCircle, Eye, Edit, Calendar, Link, MapIcon } from 'lucide-react';
import LagosWasteMap from './map';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddDumpsite, setShowAddDumpsite] = useState(false);
  const [selectedDumpsite, setSelectedDumpsite] = useState(null);
  
  // Synthetic dumpsites data for Lagos
  const [dumpsites, setDumpsites] = useState([
    {
      id: 1,
      name: "Ajegunle Collection Point",
      location: "Roofseller's Football Field, Ajegunle",
      coordinates: { lat: 6.4698, lng: 3.3528 },
      status: "Active",
      wasteTypes: ["Organic", "Plastic", "Metal", "Textile"],
      capacity: 5000,
      currentLoad: 3200,
      revenue: 1250000,
      district: "Lagos Mainland",
      dateRegistered: "2024-01-15"
    },
    {
      id: 2,
      name: "Ebute Meta Processing Hub",
      location: "Methodist Primary School Area",
      coordinates: { lat: 6.4889, lng: 3.3742 },
      status: "Re-registered",
      wasteTypes: ["Plastic", "Metal"],
      capacity: 3000,
      currentLoad: 2800,
      revenue: 980000,
      district: "Lagos Island",
      dateRegistered: "2024-02-20"
    },
    {
      id: 3,
      name: "Lekki Green Garden Center",
      location: "Sahara Green Garden Ground",
      coordinates: { lat: 6.4281, lng: 3.5186 },
      status: "Active",
      wasteTypes: ["Industrial", "Metal", "Textile"],
      capacity: 8000,
      currentLoad: 4500,
      revenue: 3200000,
      district: "Victoria Island/Lekki",
      dateRegistered: "2024-01-08"
    },
    {
      id: 4,
      name: "Apapa Industrial Zone",
      location: "Apapa Port Complex",
      coordinates: { lat: 6.4406, lng: 3.3594 },
      status: "Active",
      wasteTypes: ["Industrial", "Metal", "Hazardous"],
      capacity: 12000,
      currentLoad: 8200,
      revenue: 5400000,
      district: "Apapa",
      dateRegistered: "2023-11-12"
    },
    {
      id: 5,
      name: "Ikeja Collection Center",
      location: "Computer Village Area",
      coordinates: { lat: 6.6018, lng: 3.3515 },
      status: "Pending",
      wasteTypes: ["Electronic", "Plastic", "Metal"],
      capacity: 4000,
      currentLoad: 1200,
      revenue: 450000,
      district: "Ikeja",
      dateRegistered: "2024-07-30"
    }
  ]);

  // Waste generation data
  const wasteGenerationData = [
    { month: 'Jan', total: 220000, organic: 120000, plastic: 50000, metal: 30000, textile: 20000 },
    { month: 'Feb', total: 280000, organic: 150000, plastic: 70000, metal: 35000, textile: 25000 },
    { month: 'Mar', total: 320000, organic: 180000, plastic: 75000, metal: 40000, textile: 25000 },
    { month: 'Apr', total: 380000, organic: 200000, plastic: 90000, metal: 50000, textile: 40000 },
    { month: 'May', total: 420000, organic: 230000, plastic: 95000, metal: 55000, textile: 40000 },
    { month: 'Jun', total: 480000, organic: 260000, plastic: 110000, metal: 65000, textile: 45000 },
    { month: 'Jul', total: 520000, organic: 280000, plastic: 120000, metal: 70000, textile: 50000 },
    { month: 'Aug', total: 450000, organic: 240000, plastic: 105000, metal: 60000, textile: 45000 }
  ];

  // Pie chart data
  const wasteCompositionData = [
    { name: 'Organic', value: 55, color: '#10B981' },
    { name: 'Plastic', value: 22, color: '#F59E0B' },
    { name: 'Metal', value: 13, color: '#EF4444' },
    { name: 'Textile', value: 10, color: '#3B82F6' }
  ];

  // District performance data
  const districtData = [
    { district: 'Lagos Mainland', collections: 45000, revenue: 12500000, efficiency: 85 },
    { district: 'Lagos Island', collections: 38000, revenue: 11200000, efficiency: 78 },
    { district: 'Victoria Island/Lekki', collections: 52000, revenue: 18900000, efficiency: 92 },
    { district: 'Apapa', collections: 62000, revenue: 22100000, efficiency: 88 },
    { district: 'Ikeja', collections: 34000, revenue: 9800000, efficiency: 72 }
  ];

  // Revenue trend data
  const revenueData = [
    { month: 'Jan', revenue: 45000000, target: 50000000 },
    { month: 'Feb', revenue: 52000000, target: 55000000 },
    { month: 'Mar', revenue: 48000000, target: 52000000 },
    { month: 'Apr', revenue: 61000000, target: 58000000 },
    { month: 'May', revenue: 67000000, target: 65000000 },
    { month: 'Jun', revenue: 73000000, target: 70000000 },
    { month: 'Jul', revenue: 78000000, target: 75000000 },
    { month: 'Aug', revenue: 74000000, target: 78000000 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-green-700 bg-green-100';
      case 'Re-registered': return 'text-red-700 bg-red-100';
      case 'Pending': return 'text-yellow-700 bg-yellow-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <CheckCircle size={16} className="text-green-500" />;
      case 'Re-registered': return <AlertTriangle size={16} className="text-red-500" />;
      case 'Pending': return <XCircle size={16} className="text-yellow-500" />;
      default: return <AlertTriangle size={16} className="text-gray-500" />;
    }
  };

  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="text-blue-500" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Dumpsites</p>
              <p className="text-2xl font-bold">{dumpsites.length+256}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Trash2 className="text-green-500" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Waste (tons)</p>
              <p className="text-2xl font-bold">450</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <div className="text-green-500 font-bold text-lg">₦</div>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold">₦74M</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-purple-500" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Active Sites</p>
              <p className="text-2xl font-bold">{dumpsites.filter(d => d.status === 'Active').length +346}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Monthly Waste Generation</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={wasteGenerationData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`${(value/1000).toFixed(0)}k tons`, '']} />
              <Line type="monotone" dataKey="total" stroke="#EF4444" strokeWidth={3} />
              <Line type="monotone" dataKey="organic" stroke="#10B981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Waste Composition</h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width={300} height={300}>
              <PieChart>
                <Pie
                  data={wasteCompositionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  dataKey="value"
                >
                  {wasteCompositionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {wasteCompositionData.map((item, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full`} style={{backgroundColor: item.color}}></div>
                <span className="text-sm">{item.name} ({item.value}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Map Section */}
     
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Dumpsite Locations</h3>
        <div className="bg-gray-100 rounded-lg h-96 relative flex items-center justify-center">
       
        
          {/* Simulated map markers */}
          <div className="absolute top-4 left-4 space-y-2">
            {dumpsites.slice(0, 3).map((site) => (
              <div key={site.id} className="flex items-center space-x-2 bg-white px-3 py-2 rounded shadow-sm text-sm">
                {getStatusIcon(site.status)}
                <span>{site.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDumpsites = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Dumpsite Management</h2>
        <button 
          onClick={() => setShowAddDumpsite(true)}
          className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors"
        >
          <Plus size={20} />
          <span>Add New Dumpsite</span>
        </button>

        

        <a href ='/map' >
        <MapPin className="text-blue-500" size={24} />
        <small style={{"fontSize":8}}>Open Map</small>
        </a>
      </div>

      {/* Dumpsites List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dumpsites.map((site) => (
                <tr key={site.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{site.name}</div>
                      <div className="flex gap-1 mt-1">
                        {site.wasteTypes.map((type, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{site.location}</div>
                    <div className="text-xs text-gray-500">{site.district}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(site.status)}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(site.status)}`}>
                        {site.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{site.currentLoad.toLocaleString()} / {site.capacity.toLocaleString()} tons</div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{width: `${(site.currentLoad / site.capacity) * 100}%`}}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    ₦{site.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye size={16} />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Dumpsite Modal */}
      {showAddDumpsite && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add New Dumpsite</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Site Name</label>
                <input type="text" className="w-full border rounded-lg px-3 py-2" placeholder="Enter site name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <input type="text" className="w-full border rounded-lg px-3 py-2" placeholder="Enter location" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Capacity (tons)</label>
                <input type="number" className="w-full border rounded-lg px-3 py-2" placeholder="5000" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Waste Types</label>
                <div className="flex gap-2 flex-wrap">
                  {['Organic', 'Plastic', 'Metal', 'Textile', 'Industrial', 'Electronic'].map((type) => (
                    <label key={type} className="flex items-center space-x-1 text-sm">
                      <input type="checkbox" />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button 
                onClick={() => setShowAddDumpsite(false)}
                className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50 flex-1"
              >
                Cancel
              </button>
              <button 
                onClick={() => setShowAddDumpsite(false)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex-1"
              >
                Add Site
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderReports = () => (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold">Analytics & Reports</h2>
      
      {/* District Performance */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">District Performance</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={districtData}>
            <XAxis dataKey="district" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="collections" fill="#3B82F6" name="Collections (tons)" />
            <Bar dataKey="efficiency" fill="#10B981" name="Efficiency (%)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Trends */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Revenue vs Target</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={revenueData}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => [`₦${(value/1000000).toFixed(1)}M`, '']} />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={3} name="Actual Revenue" />
            <Line type="monotone" dataKey="target" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 5" name="Target" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <div className="text-3xl font-bold text-green-500">87%</div>
          <div className="text-gray-600 mt-2">Collection Efficiency</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <div className="text-3xl font-bold text-blue-500">23</div>
          <div className="text-gray-600 mt-2">Days Avg. Processing</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm text-center">
          <div className="text-3xl font-bold text-purple-500">₦2.3M</div>
          <div className="text-gray-600 mt-2">Revenue per Site</div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold">System Settings</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Organization Name</label>
              <input type="text" value="WasteChain Lagos" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Email</label>
              <input type="email" value="admin@wastechain.ng" className="w-full border rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input type="tel" value="+234 800 000 0000" className="w-full border rounded-lg px-3 py-2" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">System Preferences</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">Email Notifications</span>
              <input type="checkbox" defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">SMS Alerts</span>
              <input type="checkbox" defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Auto-backup Reports</span>
              <input type="checkbox" defaultChecked />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Default Currency</label>
              <select className="w-full border rounded-lg px-3 py-2">
                <option value="NGN">Nigerian Naira (₦)</option>
                <option value="USD">US Dollar ($)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Data Management</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 text-center">
            <FileText className="mx-auto mb-2" size={24} />
            <span className="text-sm">Export Data</span>
          </button>
          <button className="p-4 border border-green-500 text-green-500 rounded-lg hover:bg-green-50 text-center">
            <Calendar className="mx-auto mb-2" size={24} />
            <span className="text-sm">Schedule Reports</span>
          </button>
          <button className="p-4 border border-purple-500 text-purple-500 rounded-lg hover:bg-purple-50 text-center">
            <Users className="mx-auto mb-2" size={24} />
            <span className="text-sm">User Management</span>
          </button>
          <button className="p-4 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 text-center">
            <Settings className="mx-auto mb-2" size={24} />
            <span className="text-sm">System Backup</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex bg-gray-800 min-h-screen text-white">
      {/* Sidebar */}
      <div className="w-20 bg-gray-900 flex flex-col items-center py-6 space-y-8">
        <div className="w-10 h-10 bg-green-500 rounded flex items-center justify-center">
          <div className="w-6 h-6 bg-white rounded-sm"></div>
        </div>
        
        <div className="flex flex-col space-y-6">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex flex-col items-center space-y-1 p-2 rounded transition-colors ${
              activeTab === 'dashboard' ? 'text-green-400 bg-gray-800' : 'text-gray-400 hover:text-white'
            }`}
          >
            <BarChart3 size={20} />
            <span className="text-xs">Dashboard</span>
          </button>
          
          <button
            onClick={() => setActiveTab('dumpsites')}
            className={`flex flex-col items-center space-y-1 p-2 rounded transition-colors ${
              activeTab === 'dumpsites' ? 'text-green-400 bg-gray-800' : 'text-gray-400 hover:text-white'
            }`}
          >
            <MapPin size={20} />
            <span className="text-xs">Sites</span>
          </button>
          
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex flex-col items-center space-y-1 p-2 rounded transition-colors ${
              activeTab === 'reports' ? 'text-green-400 bg-gray-800' : 'text-gray-400 hover:text-white'
            }`}
          >
            <FileText size={20} />
            <span className="text-xs">Reports</span>
          </button>
        </div>
        
        <div className="mt-auto">
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex flex-col items-center space-y-1 p-2 rounded transition-colors ${
              activeTab === 'settings' ? 'text-green-400 bg-gray-800' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Settings size={20} />
            <span className="text-xs">Settings</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 bg-gray-100 text-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center">
              <div className="w-5 h-5 bg-white rounded-sm"></div>
            </div>
            <h1 className="text-2xl font-semibold">WasteChain Lagos</h1>
            <span className="bg-green-500 text-white px-2 py-1 rounded text-sm">CONNECTED</span>
          </div>
          <input 
            type="text" 
            placeholder="Search dumpsites, reports..." 
            className="px-4 py-2 border rounded-lg bg-white w-64"
          />
        </div>

        {/* Dynamic Content Based on Active Tab */}
        <div className="min-h-screen">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'dumpsites' && renderDumpsites()}
          {activeTab === 'reports' && renderReports()}
          {activeTab === 'settings' && renderSettings()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;